import { useState } from "react";
import { toast } from "sonner";
import { Heart, ArrowUpRight, TrendingUp, Truck, Tag } from "lucide-react";
import { saveFavorite } from "../lib/api";
import MarginCalculator from "./MarginCalculator";

export default function ProductCard({ product, rank, last, onFavoriteSaved }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleFavorite = async (e) => {
    e.preventDefault();
    if (saved || saving) return;
    setSaving(true);
    try {
      await saveFavorite(product);
      setSaved(true);
      toast.success("Saved to favorites");
      onFavoriteSaved?.();
    } catch (err) {
      toast.error("Could not save favorite");
    } finally {
      setSaving(false);
    }
  };

  const discount = product.selling_price_usd - product.cost_price_usd;

  return (
    <article
      className={`product-card bg-white flex flex-col relative ${
        !last ? "md:border-r md:border-zinc-200" : ""
      } border-b md:border-b-0 border-zinc-200`}
      data-testid={`product-card-${rank}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100 border-b border-zinc-200">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src =
              "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=800&q=80";
          }}
        />
        <div className="absolute top-3 left-3 klein-bg text-white text-xs font-black tracking-widest px-3 py-1">
          #{rank}
        </div>
        <button
          onClick={handleFavorite}
          disabled={saving || saved}
          data-testid={`favorite-btn-${rank}`}
          className="absolute top-3 right-3 w-10 h-10 bg-white border border-zinc-900 flex items-center justify-center hover:bg-zinc-100 transition"
          aria-label="Save to favorites"
        >
          <Heart
            size={18}
            strokeWidth={2.5}
            fill={saved ? "#ef4444" : "none"}
            className={saved ? "text-red-500" : "text-zinc-900"}
          />
        </button>
      </div>

      <div className="p-6 flex-1 flex flex-col">
        <div className="micro-label mb-2 klein-text">{product.problem_solved}</div>
        <h3
          className="text-xl font-black tracking-tight mb-3 leading-tight"
          style={{ fontFamily: "Outfit" }}
          data-testid={`product-name-${rank}`}
        >
          {product.name}
        </h3>
        <p className="text-sm text-zinc-600 leading-relaxed mb-5">{product.description}</p>

        {/* Big stats */}
        <div className="grid grid-cols-2 gap-0 border border-zinc-200 mb-5">
          <div className="p-4 border-r border-zinc-200">
            <div className="micro-label mb-1">Margin</div>
            <div className="text-4xl font-black tracking-tighter klein-text" style={{ fontFamily: "Outfit" }}>
              {product.profit_margin_pct}%
            </div>
          </div>
          <div className="p-4">
            <div className="micro-label mb-1">To Ontario</div>
            <div className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Outfit" }}>
              {product.shipping_days_to_ontario}
              <span className="text-base font-bold text-zinc-500 ml-1">DAYS</span>
            </div>
          </div>
        </div>

        {/* Price breakdown */}
        <div className="flex items-end justify-between mb-5 pb-5 border-b border-zinc-200">
          <div>
            <div className="micro-label mb-1">Cost</div>
            <div className="text-lg font-bold line-through text-zinc-400">
              ${product.cost_price_usd.toFixed(2)}
            </div>
          </div>
          <div className="text-center">
            <div className="micro-label mb-1">Profit</div>
            <div className="text-lg font-bold text-emerald-600">+${discount.toFixed(2)}</div>
          </div>
          <div className="text-right">
            <div className="micro-label mb-1">Sell</div>
            <div className="text-xl font-black" style={{ fontFamily: "Outfit" }}>
              ${product.selling_price_usd.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Trend */}
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={14} strokeWidth={2.5} className="text-zinc-500" />
          <span className="micro-label">Trend Score</span>
          <div className="flex-1 h-1.5 bg-zinc-200 ml-2">
            <div className="h-full klein-bg" style={{ width: `${product.trend_score}%` }} />
          </div>
          <span className="text-sm font-bold font-mono">{product.trend_score}</span>
        </div>

        {/* Hashtags */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {product.trending_hashtags.map((t) => (
            <span
              key={t}
              className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-zinc-100 border border-zinc-200 text-zinc-700"
            >
              #{t}
            </span>
          ))}
        </div>

        {/* Reasoning */}
        <div className="mb-5 p-4 bg-zinc-50 border-l-2 klein-border">
          <div className="micro-label mb-2 flex items-center gap-1.5">
            <Tag size={12} strokeWidth={2.5} /> Agent Analysis
          </div>
          <p className="text-xs text-zinc-700 leading-relaxed">{product.reasoning}</p>
        </div>

        {/* Margin Calculator */}
        <MarginCalculator product={product} rank={rank} />

        {/* Supplier */}
        <a
          href={product.supplier_url}
          target="_blank"
          rel="noreferrer"
          data-testid={`supplier-link-${rank}`}
          className="mt-auto inline-flex items-center justify-between w-full btn-outline-dark text-xs"
        >
          <span className="flex items-center gap-2">
            <Truck size={14} strokeWidth={2.5} /> VIEW ON {product.supplier_name.toUpperCase()}
          </span>
          <ArrowUpRight size={14} strokeWidth={2.5} />
        </a>
      </div>
    </article>
  );
}
